import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';

import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import ModalPortal from '../../../../components/modal-portal';
import { deleteInventoryWarehouse, updateInventoryWarehouse } from '../../../../store/inventory/inventory.api';
import { updateSellerInventoryWarehouses as updateSellerInventoryWarehouseAction } from '../../../../store/seller/seller.actions';
import style from '../inventory-warehouses.module.less';
import messages from '../messages';
import ConfigureInventoryWarehouseModal from '../modals/configure-inventory-warehouse-modal';
import DeleteModal from '../modals/delete-modal';
import ErrorModal from '../modals/error-modal';

const preventDefault = (e) => e?.preventDefault();
const MODALS = {
  configureInventoryWarehouseModal: 'ConfigureInventoryWarehouseModal',
  deleteModal: 'DeleteModal',
};

const ActionButtons = ({ inventoryWarehouseId, inventoryWarehouseName }) => {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useAsyncSafeState();
  const [errors, setErrors] = useAsyncSafeState();
  const openConfigureInventoryWarehouseModal = useCallback(() => setOpenModal(MODALS.configureInventoryWarehouseModal), [setOpenModal]);
  const openDeleteModal = useCallback(() => setOpenModal(MODALS.deleteModal), [setOpenModal]);
  const closeModal = useCallback(() => setOpenModal(undefined), [setOpenModal]);
  const closeErrorModal = useCallback(() => setErrors(undefined), [setErrors]);

  const onEdit = useCallback(
    (warehouseName) => {
      const shapedRequestBody = {
        name: warehouseName,
        inventoryWarehouseId,
      };
      return updateInventoryWarehouse(shapedRequestBody)
        .promise.then((res) => {
          dispatch(updateSellerInventoryWarehouseAction(res));
          closeModal();
        })
        .catch(setErrors);
    },
    [closeModal, dispatch, inventoryWarehouseId, setErrors],
  );

  const onDelete = useCallback(() => {
    deleteInventoryWarehouse(inventoryWarehouseId)
      .promise.then((res) => {
        if (res?.errors?.length) {
          setErrors(res.errors);
        } else {
          dispatch(updateSellerInventoryWarehouseAction(res?.inventoryWarehouses));
        }
      })
      .catch(setErrors)
      .finally(closeModal);
  }, [closeModal, dispatch, inventoryWarehouseId, setErrors]);

  return (
    <>
      <div className={style['action-buttons-wrapper']}>
        <DropdownMenu menuPosition='bottom-right'>
          <DropdownMenuToggler
            className={style['menu-toggle']}
            hideChevron
            onClick={preventDefault}
            openClassName={style['open-menu-toggle']}
            theme='link'
          >
            <div className={style['menu-toggle-contents']}>
              <FontAwesomeIcon icon={faEllipsisH} />
            </div>
          </DropdownMenuToggler>
          <DropdownMenuItem onClick={openConfigureInventoryWarehouseModal}>
            <FormattedMessage {...messages.warehousesGridEditButton} />
          </DropdownMenuItem>
          <DropdownMenuItem onClick={openDeleteModal} theme='danger-link'>
            <FormattedMessage {...messages.warehousesGridDeleteButton} />
          </DropdownMenuItem>
        </DropdownMenu>
      </div>

      {openModal === MODALS.configureInventoryWarehouseModal && (
        <ConfigureInventoryWarehouseModal inventoryWarehouseId={inventoryWarehouseId} onClose={closeModal} onSave={onEdit} />
      )}
      {openModal === MODALS.deleteModal && (
        <ModalPortal onClose={closeModal}>
          <DeleteModal inventoryWarehouseId={inventoryWarehouseId} inventoryWarehouseName={inventoryWarehouseName} onDelete={onDelete} />
        </ModalPortal>
      )}
      {errors && (
        <ModalPortal onClose={closeErrorModal}>
          <ErrorModal errors={errors} inventoryWarehouseName={inventoryWarehouseName} />
        </ModalPortal>
      )}
    </>
  );
};

ActionButtons.propTypes = {
  inventoryWarehouseId: PropTypes.string.isRequired,
  inventoryWarehouseName: PropTypes.string.isRequired,
};

export default ActionButtons;
